import { SHELL_ICON_BUTTON_ACTIVE_SX } from '@components/v2/layout/AppTemplate/components/Header/header.styles';
import Button from '@components/v2/ui/Button/Button';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import List from '@components/v2/ui/List/List';
import ListItem from '@components/v2/ui/List/ListItem';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import Popover from '@components/v2/ui/Popover/Popover';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, LinearProgress } from '@mui/material';
import { TicketPercent } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { BusinessPoliciesTestIds } from '../../../../utils/testIds/businessPolicies.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import VoucherRow from '../VoucherRow/VoucherRow';
import { VOUCHER_LIST_WIDTH } from '../VoucherRow/voucherRow.constants';
import useVoucherMenu from './hooks/useVoucherMenu';

const MENU_MAX_HEIGHT = 360;
const ITEM_PADDING = 0.75;

const VoucherMenu = () => {
  const { translate } = useTranslation();
  const {
    anchorEl,
    isVisible,
    vouchers,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    openMenu,
    closeMenu,
  } = useVoucherMenu();

  if (!isVisible) {
    return null;
  }

  const title = translate(LangEnum.BUSINESS_POLICY_VOUCHERS_MENU_TITLE);

  return (
    <>
      <Tooltip tabIndex={-1} TooltipContent={title}>
        <IconButton
          icon={TicketPercent}
          ariaLabel={title}
          ariaHasPopup
          ariaExpanded={!!anchorEl}
          isActive={!!anchorEl}
          testId={BusinessPoliciesTestIds.VOUCHER_MENU_TRIGGER}
          onClick={openMenu}
          sx={SHELL_ICON_BUTTON_ACTIVE_SX}
        />
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeMenu}
        elevation={1}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: VOUCHER_LIST_WIDTH,
              marginBlock: theme.spacing(0.5),
              borderRadius: `${theme.shape.borderRadiusXl}px`,
              border: `1px solid ${theme.palette.custom.border.primary}`,
            }),
          },
        }}
      >
        <Box sx={{ maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto', padding: ITEM_PADDING }}>
          <List>
            <ListSubheader>{title}</ListSubheader>

            {isLoading && (
              <ListItem sx={{ padding: ITEM_PADDING }}>
                <LinearProgress sx={{ flex: 1 }} />
              </ListItem>
            )}

            {vouchers.map((voucher) => (
              <ListItem key={voucher.id} sx={{ padding: ITEM_PADDING }}>
                <VoucherRow voucher={voucher} />
              </ListItem>
            ))}

            {!isLoading && vouchers.length === 0 && (
              <ListItem sx={{ padding: ITEM_PADDING, justifyContent: 'center' }}>
                <Typography component="span" variant="body2" color="text.secondary">
                  {translate(LangEnum.SNACKBAR_NO_RESULTS_FOUND)}
                </Typography>
              </ListItem>
            )}

            {hasMore && (
              <ListItem>
                <Button fullWidth size="small" loading={isLoadingMore} onClick={loadMore}>
                  {translate(LangEnum.GLOBAL_SHOW_MORE)}
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default VoucherMenu;
